// =============================================================
// LocationSelector  Phase 4
// Lets user select a Google Business Profile location for this SaaS business
// Safe rendering for loading, error, empty, and loaded states
// =============================================================

import React, { useState, useEffect } from 'react';
import { GoogleBusinessConnectionDto, GoogleBusinessLocationOption } from '../../lib/api';

interface Props {
  connectedLocation: GoogleBusinessConnectionDto | null;
  availableLocations?: GoogleBusinessLocationOption[];
  onSelectLocation: (data: { locationId: string; locationName: string; accountId?: string }) => Promise<void>;
  onRetry?: () => Promise<void>;
  loading: boolean;
  error?: string | null;
}

export const LocationSelector: React.FC<Props> = ({
  connectedLocation,
  availableLocations = [],
  onSelectLocation,
  onRetry,
  loading,
  error,
}) => {
  const safeLocations = Array.isArray(availableLocations) ? availableLocations : [];

  const [selectedLocId, setSelectedLocId] = useState<string>(() => {
    if (connectedLocation?.googleLocationId) return connectedLocation.googleLocationId;
    if (safeLocations.length > 0 && safeLocations[0]?.locationId) return safeLocations[0].locationId;
    return '';
  });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Sync selected location ID when connectedLocation or loaded locations change
  useEffect(() => {
    if (connectedLocation?.googleLocationId) {
      setSelectedLocId(connectedLocation.googleLocationId);
    } else if (!selectedLocId && safeLocations.length > 0 && safeLocations[0]?.locationId) {
      setSelectedLocId(safeLocations[0].locationId);
    }
  }, [connectedLocation?.googleLocationId, safeLocations, selectedLocId]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const loc = safeLocations.find((l) => l.locationId === selectedLocId);
    if (!loc) return;

    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    try {
      await onSelectLocation({
        locationId: loc.locationId,
        locationName: loc.locationName,
        accountId: loc.accountId,
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to select Google Business Profile location');
    } finally {
      setSaving(false);
    }
  };

  const is403 = error?.includes('403') || error?.toLowerCase().includes('approval') || error?.toLowerCase().includes('permission');
  const is429 = error?.includes('429') || error?.toLowerCase().includes('quota') || error?.toLowerCase().includes('rate limit');

  const selectedLocationObj = safeLocations.find((l) => l.locationId === selectedLocId);

  return (
    <div
      className="location-selector-box"
      style={{
        padding: '18px',
        background: '#f8fafc',
        borderRadius: '8px',
        border: '1px solid #cbd5e1',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      <div style={{ marginBottom: '14px' }}>
        <h4 style={{ margin: 0, fontSize: '0.98rem', fontWeight: 600, color: '#0f172a' }}>
          Select Google Business Profile Location
        </h4>
        <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#64748b' }}>
          Choose the Google Maps listing that corresponds to this business.
        </p>
      </div>

      {loading ? (
        <div
          style={{
            padding: '16px',
            background: '#fff',
            borderRadius: '6px',
            border: '1px solid #e2e8f0',
            fontSize: '0.88rem',
            color: '#334155',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <span
            className="spinner"
            style={{
              width: '16px',
              height: '16px',
              border: '2px solid #cbd5e1',
              borderTopColor: '#2563eb',
              borderRadius: '50%',
              display: 'inline-block',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <span style={{ fontWeight: 500 }}>Loading Google Business Profile locations...</span>
        </div>
      ) : error ? (
        <div
          style={{
            padding: '16px',
            background: '#fef2f2',
            borderRadius: '8px',
            border: '1px solid #fecaca',
            color: '#991b1b',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <span style={{ fontSize: '1.2rem' }}></span>
            <strong style={{ fontSize: '0.92rem' }}>
              {is403
                ? 'Google Business Profile Access Approval Required'
                : is429
                ? 'Google Business Profile API Quota Exceeded'
                : 'Unable to Load Business Profile Locations'}
            </strong>
          </div>
          <p style={{ margin: '0 0 12px', fontSize: '0.84rem', color: '#7f1d1d', lineHeight: 1.5 }}>
            {is403
              ? 'Google Business Profile API access may require additional approval/configuration. Note that Google requires organizational verification before API access is enabled for third-party tools.'
              : is429
              ? 'Google Business Profile API quota exceeded (limit is 0 requests/min for unverified projects). You can request quota approval in Google Cloud Console.'
              : error}
          </p>
          {onRetry && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={onRetry}
              disabled={loading}
              style={{ background: '#fff', borderColor: '#fca5a5', color: '#991b1b' }}
            >
               Retry Loading Locations
            </button>
          )}
        </div>
      ) : safeLocations.length === 0 ? (
        <div
          style={{
            padding: '16px',
            background: '#fffbeb',
            borderRadius: '8px',
            border: '1px solid #fef3c7',
            color: '#92400e',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <span></span>
            <strong style={{ fontSize: '0.92rem' }}>No Google Business Profile locations found.</strong>
          </div>
          <p style={{ margin: '0 0 12px', fontSize: '0.84rem', color: '#b45309' }}>
            No accessible locations were found for this Google account. Ensure your Google account has Owner or Manager permissions for your business listing.
          </p>
          {onRetry && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={onRetry}
              disabled={loading}
              style={{ background: '#fff', borderColor: '#fde68a', color: '#92400e' }}
            >
               Refresh Locations
            </button>
          )}
        </div>
      ) : (
        <form onSubmit={handleSave}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={selectedLocId}
              onChange={(e) => setSelectedLocId(e.target.value)}
              disabled={saving}
              className="form-input"
              style={{
                flex: 1,
                minWidth: '280px',
                padding: '9px 12px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                fontSize: '0.9rem',
              }}
            >
              {safeLocations.map((loc) => (
                <option key={loc.locationId} value={loc.locationId}>
                  {loc.locationName} {loc.address ? `(${loc.address})` : ''}  {loc.accountName}
                </option>
              ))}
            </select>

            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={saving || !selectedLocId || selectedLocId === connectedLocation?.googleLocationId}
              style={{ padding: '9px 16px', whiteSpace: 'nowrap' }}
            >
              {saving
                ? 'Connecting...'
                : connectedLocation?.googleLocationId === selectedLocId
                ? ' Connected'
                : 'Connect Location'}
            </button>
          </div>

          {/* Selected Location Preview */}
          {selectedLocationObj && (
            <div
              style={{
                marginTop: '12px',
                padding: '10px 12px',
                background: '#fff',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                fontSize: '0.82rem',
                color: '#475569',
              }}
            >
              <div>
                <strong>Selected:</strong> {selectedLocationObj.locationName}
              </div>
              {selectedLocationObj.address && (
                <div style={{ marginTop: '2px', color: '#64748b' }}>
                   {selectedLocationObj.address}
                </div>
              )}
              <div style={{ marginTop: '4px', fontSize: '0.75rem', color: '#94a3b8' }}>
                Account: {selectedLocationObj.accountName}  Location ID: {selectedLocationObj.locationId}
              </div>
            </div>
          )}

          {connectedLocation?.googleLocationName && (
            <div
              style={{
                marginTop: '10px',
                fontSize: '0.8rem',
                color: '#059669',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <span></span> Currently active connection: <strong>{connectedLocation.googleLocationName}</strong>
            </div>
          )}

          {saveSuccess && (
            <div style={{ marginTop: '10px', fontSize: '0.82rem', color: '#16a34a', fontWeight: 600 }}>
               Google Business Profile location linked successfully!
            </div>
          )}

          {saveError && (
            <div style={{ marginTop: '10px', fontSize: '0.82rem', color: '#dc2626', fontWeight: 600 }}>
               {saveError}
            </div>
          )}
        </form>
      )}

      <div
        style={{
          marginTop: '14px',
          padding: '8px 12px',
          background: '#f1f5f9',
          borderRadius: '4px',
          fontSize: '0.75rem',
          color: '#64748b',
        }}
      >
        <strong>Read-Only Notice:</strong> This integration is currently in Read-Only mode. It safely
        imports performance metrics (calls, directions, search impressions) without altering your live
        Google listing.
      </div>
    </div>
  );
};
